// Shared types for AI Agent Waterfall System

// Re-export MCP types
export * from './types/mcp';

export type AgentType =
  | "orchestrator"
  | "researcher"
  | "analyzer"
  | "writer"
  | "executor";

export type WorkflowStatus =
  | "idle"
  | "running"
  | "paused"
  | "completed"
  | "failed"
  | "cancelled";

export type AgentStatus =
  | "idle"
  | "assigned"
  | "processing"
  | "complete"
  | "error";

export type EventType =
  | "message"
  | "toolCall"
  | "toolResult"
  | "statusChange"
  | "error"
  | "artifact";

export interface WorkflowConfig {
  name: string;
  description: string;
  objective: string;
  maxDuration?: number;
  agentTypes?: AgentType[];
  parallelExecution?: boolean;
  autoStart?: boolean;
}

export interface Workflow {
  id: string;
  name: string;
  description: string;
  objective: string;
  status: WorkflowStatus;
  config: WorkflowConfig;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  userId: string;
  totalEvents: number;
  duration?: number;
}

export interface Agent {
  id: string;
  workflowId: string;
  type: AgentType;
  objective: string;
  prompt?: string;
  status: AgentStatus;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, unknown>;
  parentId?: string;
}

export interface Event {
  id: string;
  workflowId: string;
  agentId: string;
  type: EventType;
  timestamp: number;
  content: string;
  status: string;
  toolName?: string;
  metadata?: Record<string, unknown>;
  duration?: number;
  parentEventId?: string;
}

export interface WorkflowSummary {
  workflow: Workflow;
  agents: Agent[];
  eventCount: number;
  lastActivity: string;
}

// WebSocket message types
export type WebSocketMessageType =
  | "workflow.started"
  | "workflow.completed"
  | "workflow.failed"
  | "workflow.cancelled"
  | "workflow.paused"
  | "workflow.resumed"
  | "workflow.state"
  | "agent.spawned"
  | "agent.completed"
  | "agent.failed"
  | "agent.updated"
  | "event.created"
  | "error.occurred"
  | "heartbeat"
  | "subscribe"
  | "unsubscribe"
  | "connection.established";

export interface WebSocketMessage {
  type: WebSocketMessageType;
  data?: unknown;
  timestamp: number;
  workflowId?: string;
  error?: string;
}

export interface WorkflowCreateRequest {
  name: string;
  description: string;
  objective: string;
  config?: Partial<WorkflowConfig>;
}

export interface WorkflowStartRequest {
  workflowId: string;
  userId: string;
}

export interface APIResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: number;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface WorkflowListResponse {
  workflows: WorkflowSummary[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface EventListResponse {
  events: Event[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// Agent-specific objective templates
export const AGENT_OBJECTIVES: Record<AgentType, string> = {
  orchestrator: "Coordinate all agents and manage workflow execution",
  researcher:
    "Gather comprehensive data from multiple sources and validate information accuracy",
  analyzer:
    "Identify patterns, trends, and insights using statistical analysis and data processing",
  writer:
    "Create professional documents, reports, and content with proper structure and clarity",
  executor:
    "Generate visualizations, charts, and interactive elements to present findings",
};

// Agent-specific default prompts (system/role guidance)
export const AGENT_PROMPTS: Record<AgentType, string> = {
  orchestrator:
    "Role: Orchestrator\nGoals: Break down the main objective, spawn/coordinate agents, ensure quality and timely delivery.\nOperating Principles:\n- Always propose a brief plan before acting.\n- Decompose into parallelizable tasks where safe.\n- Use tools to create agents and manage workflow state.\n- Require agents to post short summaries to chat via send_to_chat.\n- Maintain an up-to-date view of progress and risks.\nDeliverables:\n- Clear task breakdown, assignments, and final integrated outcome with rationale.",
  researcher:
    "Role: Researcher\nGoals: Gather credible information, reconcile sources, and surface actionable insights aligned to the workflow objective.\nOperating Principles:\n- Cite sources; prefer primary data.\n- Reconcile conflicting claims; state confidence.\n- Highlight gaps/risks and propose next steps.\nCommunication:\n- Post concise periodic summaries to chat via send_to_chat (headlines + 3–5 bullets).\nDeliverables:\n- Sourced findings and short summaries suitable for the writer and analyzer.",
  analyzer:
    "Role: Analyzer\nGoals: Quantify trends, derive metrics, and explain patterns driving decisions.\nOperating Principles:\n- State assumptions; show key numbers.\n- Prefer simple models first; justify complexity.\n- Express uncertainty ranges and caveats.\nCommunication:\n- Post periodic summaries via send_to_chat (key metrics + implications).\nDeliverables:\n- Interpretable metrics, trend statements, and evidence for decisions.",
  writer:
    "Role: Writer\nGoals: Turn research/analysis into clear, audience-appropriate content that answers the user’s need.\nOperating Principles:\n- Structure with headings; keep concise and skimmable.\n- Use consistent tone; avoid hedging unless needed.\n- Include callouts/tables when clarity improves.\nCommunication:\n- Post progress and interim drafts via send_to_chat.\nDeliverables:\n- Polished sections or summaries ready for the end user.",
  executor:
    "Role: Executor (Builder/Visualizer)\nGoals: Produce functional artifacts (charts, dashboards, code, assets) that support the objective.\nOperating Principles:\n- Optimize for clarity, accessibility, and performance.\n- Provide minimal, documented configs or instructions.\n- Validate outputs against requirements.\nCommunication:\n- Post quick previews/summaries via send_to_chat.\nDeliverables:\n- Working artifacts with brief usage notes.",
};

// Color schemes for visualization
export const AGENT_COLORS: Record<AgentType, string> = {
  orchestrator: "#8b5cf6",
  researcher: "#06b6d4",
  analyzer: "#10b981",
  writer: "#f59e0b",
  executor: "#ef4444",
};

export const EVENT_COLORS: Record<EventType, string> = {
  message: "#6b7280",
  toolCall: "#3b82f6",
  toolResult: "#10b981",
  statusChange: "#8b5cf6",
  error: "#ef4444",
  artifact: "#22c55e",
};

// Validation helpers
export function isValidAgentType(type: string): type is AgentType {
  return [
    "orchestrator",
    "researcher",
    "analyzer",
    "writer",
    "executor",
  ].includes(type);
}

export function isValidWorkflowStatus(
  status: string,
): status is WorkflowStatus {
  return [
    "idle",
    "running",
    "paused",
    "completed",
    "failed",
    "cancelled",
  ].includes(status);
}

export function isValidEventType(type: string): type is EventType {
  return [
    "message",
    "toolCall",
    "toolResult",
    "statusChange",
    "error",
  ].includes(type);
}

// Error types
export class WorkflowError extends Error {
  constructor(
    message: string,
    public code: string,
    public workflowId?: string,
    public agentId?: string,
  ) {
    super(message);
    this.name = "WorkflowError";
  }
}

export class AgentError extends Error {
  constructor(
    message: string,
    public code: string,
    public agentId: string,
    public workflowId?: string,
  ) {
    super(message);
    this.name = "AgentError";
  }
}

export class AuthError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 401,
  ) {
    super(message);
    this.name = "AuthError";
  }
}

export class TokenError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 401,
  ) {
    super(message);
    this.name = "TokenError";
  }
}

// Constants
export const MAX_WORKFLOW_DURATION = 300; // 5 minutes in seconds
export const MAX_EVENTS_PER_WORKFLOW = 1000;
export const MAX_CONCURRENT_WORKFLOWS_PER_USER = 10;
export const WEBSOCKET_HEARTBEAT_INTERVAL = 30000; // 30 seconds
export const EVENT_BATCH_SIZE = 10;
export const DEFAULT_PAGE_SIZE = 20;

// User and Authentication Types
export interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  verified_email: boolean;
  created_at: string;
  last_login: string;
}

export interface Session {
  token: string;
  expires_at: number;
  user: User;
}

export interface JWTPayload {
  userId: string;
  email: string;
  name: string;
  exp: number;
  iat: number;
}

export interface OAuthState {
  state: string;
  created_at: number;
  expires_at: number;
}

// Cloudflare Worker Environment Types
export interface Env {
  WORKFLOWS_KV: KVNamespace;
  EVENTS_KV: KVNamespace;
  AGENTS_KV: KVNamespace;
  CLAUDE_API_KEY: string;
  OPENAI_API_KEY?: string;
  WORKFLOW_ORCHESTRATOR: DurableObjectNamespace;
  AGENT_WORKER: DurableObjectNamespace;
  USER: DurableObjectNamespace;
  GOOGLE_CLIENT_ID: string;
  GOOGLE_CLIENT_SECRET: string;
  JWT_SECRET: string;
  DB?: D1Database;
  AI?: Ai;
  VECTORIZE?: VectorizeIndex;
  HYPERDRIVE?: Hyperdrive;
  R2_BUCKET?: R2Bucket;
  QUEUE?: Queue;
}

// Claude API Types
export interface ClaudeMessage {
  role: "user" | "assistant" | "system";
  content: string | ClaudeContent[];
}

export interface ClaudeContent {
  type: "text" | "tool_use" | "tool_result";
  text?: string;
  id?: string;
  name?: string;
  input?: Record<string, unknown>;
  content?: string | unknown[];
  is_error?: boolean;
}

export interface ClaudeToolDefinition {
  name: string;
  description: string;
  input_schema: {
    type: "object";
    properties: Record<string, unknown>;
    required?: string[];
  };
}

export interface ClaudeAPIRequest {
  model: string;
  max_tokens: number;
  temperature?: number;
  messages: ClaudeMessage[];
  tools?: ClaudeToolDefinition[];
  tool_choice?: "auto" | "any" | { type: "tool"; name: string };
  system?: string;
  stream?: boolean;
}

export interface ClaudeAPIResponse {
  id: string;
  type: "message";
  role: "assistant";
  content: ClaudeContent[];
  model: string;
  stop_reason: "end_turn" | "max_tokens" | "stop_sequence" | "tool_use" | null;
  stop_sequence: string | null;
  usage: {
    input_tokens: number;
    output_tokens: number;
  };
}

// Tool execution types
export interface ToolCall {
  id: string;
  name: string;
  input: Record<string, unknown>;
}

export interface ToolResult {
  id: string;
  content: string;
  is_error?: boolean;
  metadata?: Record<string, unknown>;
}

// Agent task input types
export interface OrchestrationInput {
  objective: string;
  required_agents?: string[];
  constraints?: string[];
  priority?: "low" | "medium" | "high";
}

export interface ResearchInput {
  task: string;
  sources?: string[];
  depth?: "shallow" | "deep";
  focus_areas?: string[];
}

export interface AnalysisInput {
  data: unknown;
  analysis_type: "statistical" | "trend" | "pattern" | "comparative";
  metrics?: string[];
  timeframe?: string;
}

export interface WritingInput {
  content: unknown;
  template: "executive" | "technical" | "marketing" | "academic";
  tone?: "formal" | "casual" | "persuasive";
  length?: "brief" | "detailed" | "comprehensive";
}

export interface ExecutionInput {
  task: string;
  format: "chart" | "dashboard" | "report" | "visualization";
  data?: unknown;
  styling?: Record<string, unknown>;
}
